import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Download } from '@mui/icons-material';
import { IPagination, PaginationInfo } from '../../types/general.types';
import { getDateTimeFormat, getActionsColumn } from '../../utils';
import { useSortDataGrid } from '../../utils/hooks';
import { PAGINATION_INITIAL_PAGE_INDEX } from '../../constants';
import LoadingComponent from '../common/LoadingComponent';
import ErrorDialog from '../common/ErrorDialog';
import DataGridComponent from '../common/DataGrid';
import { CCTVVideoRequest } from '../../types/cctv.types';
import { getVideoRequestStatusText } from '../../utils/cctv';

export interface CCTVTemplateProps {
  data: { data: CCTVVideoRequest[]; totalCount: number };
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  onPageChange: (pageNumber: number) => void;
  pageNumber: number;
  setQuerySort: (sort: string) => void;
  setQueryOrderBy: (orderBy: string) => void;
  onDownload: (row: CCTVVideoRequest) => void;
}

export default function CCTVVideoRequestsTemplate({
  data,
  isLoading,
  isError,
  error,
  pageSize,
  onPageSizeChange,
  onPageChange,
  pageNumber,
  setQueryOrderBy,
  setQuerySort,
  onDownload,
}: CCTVTemplateProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(PAGINATION_INITIAL_PAGE_INDEX);

  const { onSortModelChange, sortModel } = useSortDataGrid({
    setQuerySort,
    setQueryOrderBy,
  });

  let pagination: IPagination | {} = {};
  if (data) {
    pagination = {
      totalCount: data.totalCount,
      pageSize,
      currentPage: pageNumber,
      hasNext: (pageNumber - 1) * pageSize + pageSize < data.totalCount,
      hasPrevious: pageNumber > 1,
      totalPages: Math.ceil(data.totalCount / pageSize),
    };
  }

  const onChangePageSize = (newPageSize: number) => {
    onPageSizeChange(newPageSize);
  };

  const onPageChangeHandler = (pageIndex: number) => {
    setPage(pageIndex);
    onPageChange(pageIndex + 1);
  };

  const getRows = () => {
    return (data?.data || []).map((el: CCTVVideoRequest) => {
      const row = {
        ...el,
        status: getVideoRequestStatusText(el.status),
        requestDate: getDateTimeFormat(el.requestDate),
      };
      return row;
    });
  };

  const renderActions = (params: GridRowModel) => {
    const { status } = params.row;
    if (status === 'Executed') {
      return (
        <Tooltip title={t('download_video_archive') as string}>
          <IconButton
            onClick={() => onDownload(params.row as CCTVVideoRequest)}
            aria-label="Download video"
          >
            <Download />
          </IconButton>
        </Tooltip>
      );
    }

    return <Box />;
  };

  const columns: GridColDef[] = [
    {
      field: 'lockerCode',
      headerName: t('locker_code') as string,
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: 'requestDate',
      headerName: t('created_at') as string,
      flex: 1,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'status',
      headerName: t('status') as string,
      flex: 1,
      disableColumnMenu: true,
    },
    getActionsColumn(renderActions),
  ];

  return (
    <div>
      {isLoading && <LoadingComponent />}
      {isError && <ErrorDialog status message={error as string} />}
      {data && (
        <DataGridComponent
          columns={columns}
          rows={getRows()}
          rowCount={(pagination as IPagination).totalCount}
          sortingMode="server"
          onSortModelChange={onSortModelChange}
          sortModel={sortModel}
          getId={(row: GridRowModel) => row.id}
          paginationMode="server"
          pageSize={pageSize}
          onChangePageSize={onChangePageSize}
          onPageChange={onPageChangeHandler}
          page={page}
          disableSelectionOnClick
        />
      )}
    </div>
  );
}
